import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Wallet, Clock, TrendingUp } from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  ResponsiveContainer,
  Tooltip,
  CartesianGrid,
  Cell,
} from 'recharts';
import { FinanceTab } from '@/types';
import { useFetchFinanceOverview } from '@/hooksApi';
import usePermission from '@/hooks/usePermission';
import { PermissionName } from '@/types';
import { formatWithThousandSeparator } from '@/utils';
import useUserStore from '@/stores/userStore';
import { AdvanceCodPromoCard } from '@/components/AdvanceCod';

interface BalanceProps {
  setActiveTab: (tab: FinanceTab) => void;
}

function shortenAmount(value: number): string {
  if (value >= 1_000_000_000) return `${(value / 1_000_000_000).toFixed(1)}B`;
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `${(value / 1_000).toFixed(1)}K`;
  return formatWithThousandSeparator(value);
}

const PERIOD_OPTIONS = ['daily', 'weekly', 'monthly'] as const;
type PeriodType = (typeof PERIOD_OPTIONS)[number];

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const Balance = ({ setActiveTab: _setActiveTab }: BalanceProps) => {
  const { t } = useTranslation();
  const { hasPermission } = usePermission();
  const [chartPeriod, setChartPeriod] = useState<PeriodType>('daily');

  const { data, isLoading } = useFetchFinanceOverview(
    hasPermission(PermissionName.FINANCE),
    chartPeriod,
  );

  const overview = data?.object;
  const availableCOD = overview?.available_cod ?? 0;
  const pendingCOD = overview?.pending_cod ?? 0;
  const totalSales = overview?.total_sales ?? 0;
  const chartData = overview?.chart ?? [];

  // Advance COD activation promo (R1) — offered on the Balance tab to merchants
  // who hold the Advance COD permission but are NOT yet enrolled (no financing
  // row). Gating on ADVANCE_COD (not FINANCE) keeps a permission-less sub-user
  // from ever mounting the promo — no privileged summary request fires — while
  // the card still self-hides on a 403 as defense-in-depth.
  const user = useUserStore((state) => state.user);
  // `features.advance_cod === false` means the product is RETIRED, which is a
  // different thing from this merchant not being enrolled — both show up as
  // `advance_cod: null`. Without this the promo kept inviting merchants to
  // activate a product whose sign-up endpoint now answers 404. Absent block =
  // older API = on, so nothing changes for a backend that does not send it.
  const advanceCodOffered = user?.features?.advance_cod !== false;
  const showPromo =
    advanceCodOffered && hasPermission(PermissionName.ADVANCE_COD) && !user?.advance_cod;

  return (
    <div className='flex flex-col gap-4'>
      {showPromo ? <AdvanceCodPromoCard /> : null}

      {/* 3-column balance cards */}
      <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
        {/* Available Balance */}
        <div className='bg-white rounded-2xl border border-[rgb(232,230,227)] p-5'>
          <div className='flex items-center gap-2 mb-3'>
            <Wallet className='w-4 h-4' style={{ color: 'rgb(187, 120, 27)' }} />
            <span className='text-[13px] font-medium text-[rgb(115,123,140)]'>
              {String(t('available_balance'))}
            </span>
          </div>
          <div className='flex items-baseline gap-1.5'>
            {isLoading ? (
              <div className='h-8 w-32 bg-[rgb(232,230,227)] animate-pulse rounded' />
            ) : (
              <>
                <span className='text-[26px] font-bold text-[rgb(57,57,60)] leading-none'>
                  {formatWithThousandSeparator(availableCOD)}
                </span>
                <span className='text-[13px] font-medium text-[rgb(115,123,140)]'>
                  {String(t('iqd'))}
                </span>
              </>
            )}
          </div>
        </div>

        {/* Pending Balance */}
        <div className='bg-white rounded-2xl border border-[rgb(232,230,227)] p-5'>
          <div className='flex items-center gap-2 mb-3'>
            <Clock className='w-4 h-4' style={{ color: 'rgb(245, 158, 11)' }} />
            <span className='text-[13px] font-medium text-[rgb(115,123,140)]'>
              {String(t('pending_balance'))}
            </span>
          </div>
          <div className='flex items-baseline gap-1.5'>
            {isLoading ? (
              <div className='h-8 w-32 bg-[rgb(232,230,227)] animate-pulse rounded' />
            ) : (
              <>
                <span className='text-[26px] font-bold text-[rgb(57,57,60)] leading-none'>
                  {formatWithThousandSeparator(pendingCOD)}
                </span>
                <span className='text-[13px] font-medium text-[rgb(115,123,140)]'>
                  {String(t('iqd'))}
                </span>
              </>
            )}
          </div>
        </div>

        {/* Total Sales */}
        <div className='bg-white rounded-2xl border border-[rgb(232,230,227)] p-5'>
          <div className='flex items-center gap-2 mb-3'>
            <TrendingUp className='w-4 h-4' style={{ color: 'rgb(34, 197, 94)' }} />
            <span className='text-[13px] font-medium text-[rgb(115,123,140)]'>
              {String(t('total_sales', { defaultValue: 'Total Sales' }))}
            </span>
          </div>
          <div className='flex items-baseline gap-1.5'>
            {isLoading ? (
              <div className='h-8 w-32 bg-[rgb(232,230,227)] animate-pulse rounded' />
            ) : (
              <>
                <span className='text-[26px] font-bold text-[rgb(57,57,60)] leading-none'>
                  {shortenAmount(totalSales)}
                </span>
                <span className='text-[13px] font-medium text-[rgb(115,123,140)]'>
                  {String(t('iqd'))}
                </span>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Sales Overview Chart */}
      <div className='bg-white rounded-2xl border border-[rgb(232,230,227)] p-5'>
        <div className='flex items-center justify-between mb-4'>
          <span className='text-[14px] font-semibold text-[rgb(57,57,60)]'>
            {String(t('sales_overview', { defaultValue: 'Sales Overview' }))}
          </span>
          <div className='flex items-center gap-1 bg-[rgb(245,245,243)] rounded-lg p-0.5'>
            {PERIOD_OPTIONS.map((p) => (
              <button
                key={p}
                type='button'
                onClick={() => setChartPeriod(p)}
                className={`px-3 py-1.5 rounded-md text-[12px] font-semibold cursor-pointer transition-all ${
                  chartPeriod === p
                    ? 'bg-white text-[rgb(57,57,60)] shadow-sm'
                    : 'bg-transparent text-[rgb(115,123,140)] hover:text-[rgb(57,57,60)]'
                }`}
              >
                {String(t(p, { defaultValue: p.charAt(0).toUpperCase() + p.slice(1) }))}
              </button>
            ))}
          </div>
        </div>

        {chartData.length === 0 ? (
          <div className='flex items-center justify-center h-[200px] text-[13px] text-[rgb(115,123,140)]'>
            {isLoading ? (
              <div className='h-6 w-6 rounded-full border-2 border-[rgb(237,224,74)] border-t-transparent animate-spin' />
            ) : (
              String(t('no_sales_data', { defaultValue: 'No sales data available yet' }))
            )}
          </div>
        ) : (
          <div style={{ height: 300 }}>
            <ResponsiveContainer width='100%' height='100%'>
              <BarChart data={chartData} barCategoryGap='20%'>
                <CartesianGrid strokeDasharray='3 3' vertical={false} stroke='rgb(232,230,227)' />
                <XAxis
                  dataKey='label'
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 11, fill: 'rgb(115,123,140)' }}
                />
                <YAxis
                  axisLine={false}
                  tickLine={false}
                  tickCount={5}
                  tick={{ fontSize: 10, fill: 'rgb(115,123,140)' }}
                  tickFormatter={(v: number) =>
                    v >= 1000 ? `${(v / 1000).toFixed(1)}K` : String(v)
                  }
                  width={40}
                />
                <Tooltip
                  cursor={{ fill: 'rgba(245,245,243,0.5)' }}
                  contentStyle={{
                    background: 'rgb(255,255,255)',
                    border: '1px solid rgb(232,230,227)',
                    borderRadius: 10,
                    fontSize: 12,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                  }}
                  formatter={(value: number) => [`${value.toLocaleString()} IQD`, '']}
                  labelFormatter={(label: string) => label}
                />
                <Bar dataKey='value' radius={[6, 6, 0, 0]}>
                  {chartData.map((entry, index) => {
                    const maxVal = Math.max(...chartData.map((d) => d.value));
                    return (
                      <Cell
                        key={index}
                        fill={entry.value === maxVal ? 'rgb(237,224,74)' : 'rgba(237,224,74,0.35)'}
                      />
                    );
                  })}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </div>
  );
};

export default Balance;
